import React, { useState } from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import { requestTypeLabel } from './scheduler-ui.js';
import { apiWrite } from '../../lib/api-client.js';
import type { SchedulerDriver, TimeOffRequest } from './use-scheduler-read-model.js';

interface RequestRowProps {
  request: TimeOffRequest;
  onDecide?: () => void;
}

function RequestRow({ request, onDecide }: RequestRowProps) {
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showDenyNotes, setShowDenyNotes] = useState(false);
  const [denyNotes, setDenyNotes] = useState('');
  const isPending = request.status === 'pending';

  async function handleDecide(decision: string) {
    setBusy(true);
    setActionError(null);
    const payload: { decision: string; notes?: string } = { decision };
    if (decision === 'denied' && denyNotes.trim()) {
      payload.notes = denyNotes.trim();
    }
    const { ok, data } = await apiWrite<{ error?: string }>(`/api/scheduler/time-off/${request.id}/decide`, 'PATCH', payload);
    setBusy(false);
    if (!ok) {
      setActionError(data?.error || 'Action failed');
      return;
    }
    setShowDenyNotes(false);
    setDenyNotes('');
    if (onDecide) onDecide();
  }

  return (
    <div style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}`, display: 'grid', gap: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{request.employee_name || 'Unknown employee'}</div>
        <div style={{ color: request.status === 'approved' ? C.green : request.status === 'denied' ? C.red : C.gold, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          {request.status || 'pending'}
        </div>
      </div>
      <div style={{ color: C.textMuted, fontSize: 12 }}>{requestTypeLabel(request.request_type)} · {request.start_date} → {request.end_date}</div>
      {request.reason ? <div style={{ color: C.textMuted, fontSize: 12 }}>{request.reason}</div> : null}
      {request.decision_notes ? <div style={{ color: C.textMuted, fontSize: 11, fontStyle: 'italic' }}>Note: {request.decision_notes}</div> : null}
      {isPending && onDecide ? (
        <>
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button
              disabled={busy}
              onClick={() => handleDecide('approved')}
              style={{
                padding: '6px 14px', borderRadius: 6, border: 'none', cursor: busy ? 'not-allowed' : 'pointer',
                background: C.green, color: '#fff', fontSize: 12, fontWeight: 600, opacity: busy ? 0.6 : 1,
              }}
            >
              {busy ? '...' : 'Approve'}
            </button>
            <button
              disabled={busy}
              onClick={() => {
                if (showDenyNotes) {
                  handleDecide('denied');
                } else {
                  setShowDenyNotes(true);
                }
              }}
              style={{
                padding: '6px 14px', borderRadius: 6, border: 'none', cursor: busy ? 'not-allowed' : 'pointer',
                background: C.red, color: '#fff', fontSize: 12, fontWeight: 600, opacity: busy ? 0.6 : 1,
              }}
            >
              {busy ? '...' : showDenyNotes ? 'Confirm deny' : 'Deny'}
            </button>
            {showDenyNotes ? (
              <button
                type="button"
                onClick={() => { setShowDenyNotes(false); setDenyNotes(''); }}
                style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.borderSoft}`, background: 'transparent', color: C.textMuted, fontSize: 12, cursor: 'pointer' }}
              >
                Cancel
              </button>
            ) : null}
          </div>
          {showDenyNotes ? (
            <input
              type="text"
              value={denyNotes}
              onChange={(e) => setDenyNotes(e.target.value)}
              placeholder="Denial reason (optional)..."
              style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.borderSoft}`, background: 'rgba(7,11,18,0.72)', color: C.text, fontSize: 12, marginTop: 4, boxSizing: 'border-box' }}
              autoFocus
            />
          ) : null}
        </>
      ) : null}
      {actionError ? <div style={{ color: C.red, fontSize: 11, marginTop: 2 }}>{actionError}</div> : null}
    </div>
  );
}

interface SchedulerTimeOffPanelProps {
  pendingRequests: TimeOffRequest[];
  decidedRequests: TimeOffRequest[];
  ptoEligible: SchedulerDriver[];
  onDecide: () => void;
}

export default function SchedulerTimeOffPanel({ pendingRequests, decidedRequests, ptoEligible, onDecide }: SchedulerTimeOffPanelProps) {
  return (
    <div style={LAYOUT.subPanel}>
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 12 }}>
        Time off / PTO
      </div>

      <div style={{ display: 'grid', gap: 16 }}>
        <div>
          <div style={{ color: C.text, fontSize: 14, marginBottom: 8 }}>Pending requests ({pendingRequests.length})</div>
          <div style={{ display: 'grid', gap: 8 }}>
            {pendingRequests.length ? pendingRequests.map((request: TimeOffRequest) => <RequestRow key={request.id} request={request} onDecide={onDecide} />) : <div style={{ color: C.textMuted, fontSize: 12 }}>No pending requests.</div>}
          </div>
        </div>

        <div>
          <div style={{ color: C.text, fontSize: 14, marginBottom: 8 }}>Recent decisions ({decidedRequests.length})</div>
          <div style={{ display: 'grid', gap: 8 }}>
            {decidedRequests.length ? decidedRequests.slice(0, 10).map((request: TimeOffRequest) => <RequestRow key={request.id} request={request} />) : <div style={{ color: C.textMuted, fontSize: 12 }}>No decided requests yet.</div>}
          </div>
        </div>

        <div>
          <div style={{ color: C.text, fontSize: 14, marginBottom: 8 }}>PTO balances</div>
          {ptoEligible.length ? (
            <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
              {ptoEligible.map((driver: SchedulerDriver) => {
                const balance = driver.pto_balance || { annual_days: 0, used_days: 0 };
                const remaining = (balance.annual_days || 0) - (balance.used_days || 0);
                return (
                  <div key={driver.id} style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
                    <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{driver.first_name} {driver.last_name}</div>
                    <div style={{ color: C.textMuted, fontSize: 12, marginTop: 4 }}>Annual: {balance.annual_days || 0}</div>
                    <div style={{ color: C.textMuted, fontSize: 12 }}>Used: {balance.used_days || 0}</div>
                    <div style={{ color: remaining > 0 ? C.gold : C.red, fontSize: 12, marginTop: 4 }}>Remaining: {remaining}</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ color: C.textMuted, fontSize: 12 }}>No PTO-eligible employees found.</div>
          )}
        </div>
      </div>
    </div>
  );
}
